import React from "react";

import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import type { RebookSliceHeaderProps } from "./types";

export const useRebookSliceHeader = ({
  slice,
  airports,
  travelersCount,
}: RebookSliceHeaderProps) => {
  const { t, formatDateTime } = useI18nContext();

  const { firstSegment, lastSegment } = React.useMemo(() => {
    const { segments } = slice;
    return {
      firstSegment: segments[0],
      lastSegment: segments[segments.length - 1],
    };
  }, [slice]);

  const { origin, destination } = React.useMemo(() => {
    const originCode = firstSegment.origin.locationCode;
    const originName = airports[originCode]?.cityName ?? originCode;
    const destinationCode = lastSegment.destination.locationCode;
    const destinationName =
      airports[destinationCode]?.cityName ?? destinationCode;
    return {
      origin: { code: originCode, name: originName },
      destination: { code: destinationCode, name: destinationName },
    };
  }, [
    airports,
    firstSegment.origin.locationCode,
    lastSegment.destination.locationCode,
  ]);

  const formattedDepartureDate = React.useMemo(() => {
    const departureDate =
      firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture;
    return formatDateTime(
      new Date(departureDate),
      DateTimeFormatStyle.MediumDate
    );
  }, [firstSegment, formatDateTime]);

  return React.useMemo(
    () => ({
      text: {
        travelers: t("core-flights.itinerary.travelers.travelers", {
          count: travelersCount,
        }),
        origin,
        destination,
        departureDate: formattedDepartureDate,
        cabinClass: firstSegment.cabinClassName,
      },
    }),
    [
      origin,
      destination,
      firstSegment.cabinClassName,
      formattedDepartureDate,
      travelersCount,
      t,
    ]
  );
};
